import { useEmailStore, type Email } from '@/stores/emailStore'
import { useTimelineActivityStore } from '@/stores/timelineActivityStore'
import { useEmailSubscriptionStore } from '@/stores/emailSubscriptionStore'
import { EmailModel } from '@/models/EmailModel'
import { createEmail } from '@/factories/emailFactory'
import { usePersonStore } from '@/stores/v2/person'
import { useOrganizationStore } from '@/stores/v2/organization'
import type { Person } from '@/types/Person'
import type { Organization } from '@/types/Organization'
import type { EmailSubscription } from '@/types/email'
/**
 * Email Service - Provides business logic for email operations
 * Uses the email store for state management and persistence
 */
export function useEmailService() {
  const emailStore = useEmailStore()
  const personStore = usePersonStore()
  const organizationStore = useOrganizationStore()
  const emailSubscriptionStore = useEmailSubscriptionStore()

  return {
    /**
     * Send an email to a single recipient
     * Validates the email data, sets default values, and persists to the store
     */
    async sendEmail(data: Partial<Email>): Promise<string> {
      // Ensure we're not trying to send to multiple recipients
      if (Array.isArray(data.to) && data.to.length > 1) {
        throw new Error(
          'sendEmail only supports a single recipient. Use sendEmailToList or sendEmailToRecipients for multiple recipients.',
        )
      }

      const recipient = Array.isArray(data.to) ? data.to[0] : data.to
      if (!recipient) {
        throw new Error('No recipient specified')
      }

      const email = createEmail({
        ...data,
        to: recipient,
        sentAt: new Date(),
      })

      // Validate using the model
      if (!EmailModel.validate(email)) {
        throw new Error('Invalid email data')
      }

      // Add to store
      const sentEmail = await emailStore.add(email)

      // Add to timeline
      await addEmailToTimeline(sentEmail)

      return sentEmail.id || ''
    },

    /**
     * Send an email to multiple recipients
     * Creates individual email documents for each recipient
     */
    async sendEmailToRecipients(
      baseEmail: Omit<Partial<Email>, 'to'>,
      recipients: Array<{
        email: string
        personId?: string
        organizationId?: string
      }>,
    ): Promise<string[]> {
      if (!recipients.length) {
        throw new Error('No recipients specified')
      }

      const emailLogIds: string[] = []

      // Send to each recipient individually
      for (const recipient of recipients) {
        const emailData = {
          ...baseEmail,
          to: recipient.email,
          personId: recipient.personId,
          organizationId: recipient.organizationId,
        }

        const emailLogId = await this.sendEmail(emailData)
        emailLogIds.push(emailLogId)
      }

      return emailLogIds
    },

    /**
     * Send an email to all active subscribers of an email list
     */
    async sendEmailToList(
      baseEmail: Omit<Partial<Email>, 'to'>,
      listId: string,
    ): Promise<string[]> {
      // Get all active subscribers
      const subscriptions = await emailSubscriptionStore.query([
        { field: 'listId', op: '==', value: listId },
        { field: 'status', op: '==', value: 'active' },
      ])

      if (!subscriptions.length) {
        throw new Error('No active subscribers found for this list')
      }

      // Map subscriptions to recipient format
      const recipients = subscriptions.map((sub: EmailSubscription) => ({
        email: sub.email,
        personId: sub.personId,
        organizationId: sub.organizationId,
      }))

      // Send to all recipients
      return this.sendEmailToRecipients(baseEmail, recipients)
    },

    /**
     * Mark an email for retry
     * Updates the email's delivery state to RETRY
     */
    async retryEmail(id: string): Promise<boolean> {
      try {
        await emailStore.update(id, {
          delivery: {
            state: 'RETRY',
          },
        } as Partial<Email>)
        return true
      } catch (error) {
        console.error('Failed to mark email for retry:', error)
        return false
      }
    },

    /**
     * Get emails by recipient
     * Queries for emails sent to a specific recipient
     */
    async getEmailsByRecipient(email: string): Promise<Email[]> {
      return await emailStore.query([{ field: 'to', op: '==', value: email }])
    },

    /**
     * Get emails by delivery state
     * Queries for emails with a specific delivery state
     */
    async getEmailsByDeliveryState(state: string): Promise<Email[]> {
      return await emailStore.query([{ field: 'delivery.state', op: '==', value: state }])
    },

    /**
     * Get emails for a specific person
     */
    async getEmailsForPerson(personId: string): Promise<Email[]> {
      await emailStore.load()
      return emailStore.findBy((email: Email) => email.personId === personId)
    },

    /**
     * Get emails for a specific organization
     */
    async getEmailsForOrganization(organizationId: string): Promise<Email[]> {
      await emailStore.load()
      return emailStore.findBy((email: Email) => email.organizationId === organizationId)
    },

    /**
     * Get the most recent emails
     * @param limit The maximum number of emails to return (default: 20)
     */
    async getRecentEmails(limit: number = 20): Promise<Email[]> {
      return await emailStore.query([], ['sentAt', 'desc'], limit)
    },

    /**
     * Get organizations that have either their own email or contacts with email
     * @returns Array of organizations with formatted display names
     */
    async getEmailableOrganizations(): Promise<
      { id: string; name: string; email: string | undefined }[]
    > {
      const orgSearch = await organizationStore.search()
      const personSearch = await personStore.search()

      const [orgsWithEmail, peopleWithEmail] = await Promise.all([
        orgSearch.withEmail().run(),
        personSearch.withEmail().run(),
      ])

      // Collect organizationIds referenced by people that have emails
      const orgIdsFromPeople = new Set(
        peopleWithEmail
          .map((p: Person) => p.organizationId)
          .filter((id: string | undefined): id is string => Boolean(id)),
      )

      // Fetch any organizations not already in orgsWithEmail list
      const existingOrgIds = new Set(orgsWithEmail.map((o: Organization) => o.id))
      const missingOrgIds = Array.from(orgIdsFromPeople).filter((id) => !existingOrgIds.has(id))
      const missingOrgs = await Promise.all(missingOrgIds.map((id) => organizationStore.get(id)))
      const allOrgs: Organization[] = [
        ...orgsWithEmail,
        ...missingOrgs.filter((o): o is Organization => Boolean(o)),
      ]

      // Deduplicate by id and format
      const dedupedMap = new Map<string, Organization>()
      for (const org of allOrgs) {
        if (org.id) {
          dedupedMap.set(org.id, org)
        }
      }

      return Array.from(dedupedMap.values())
        .filter((org): org is Organization & { id: string } => Boolean(org.id))
        .map((org) => ({
          id: org.id,
          name: org.name + (org.email ? ` (${org.email})` : ''),
          email: org.email,
        }))
    },

    /**
     * Get people with email addresses, optionally filtered by organization
     * @param query Optional search query to filter results
     * @param organizationId Optional organization ID to filter by
     * @returns Array of people with formatted display names
     */
    async getEmailablePeople(
      query?: string,
      organizationId?: string,
    ): Promise<{ id: string; name: string; email: string; organizationId?: string }[]> {
      const personSearch = await personStore.search()
      if (organizationId) personSearch.byOrganization(organizationId)
      const peopleWithEmail = await personSearch.withEmail().run()

      let people = peopleWithEmail

      if (query) {
        const lowercaseQuery = query.toLowerCase()
        people = people.filter(
          (person) =>
            person.firstName.toLowerCase().includes(lowercaseQuery) ||
            person.lastName.toLowerCase().includes(lowercaseQuery) ||
            (person.email && person.email.toLowerCase().includes(lowercaseQuery)),
        )
      }

      return people.map((p: Person) => ({
        id: p.id!,
        name: `${p.firstName} ${p.lastName} (${p.email})`,
        email: p.email!,
        organizationId: p.organizationId,
      }))
    },

    /**
     * Check if an organization or person has any email addresses
     * For organizations, checks both the org's email and its people's emails
     * For people, checks their email
     */
    async hasEmailAddresses(personId?: string, organizationId?: string): Promise<boolean> {
      if (organizationId) {
        // Check organization email
        const org = await organizationStore.get(organizationId)
        if (org?.email && org.email.trim() !== '') {
          return true
        }

        // Check people's emails via search
        const personSearch = await personStore.search()
        const count = await personSearch.byOrganization(organizationId).withEmail().count()
        return count > 0
      } else if (personId) {
        const person = await personStore.get(personId)
        return !!(person?.email && person.email.trim() !== '')
      }
      return false
    },

    /**
     * Search organizations with email addresses
     * @param query Search query to filter results
     * @returns Array of organizations matching the query
     */
    async searchOrganizations(
      query: string,
    ): Promise<{ id: string; name: string; email: string }[]> {
      const orgSearch = await organizationStore.search()
      const orgs = await orgSearch.byText(query).run()
      return orgs
        .filter((org: Organization): org is Organization & { id: string; email: string } =>
          Boolean(org.id && org.email && org.email.trim() !== ''),
        )
        .map((org) => ({ id: org.id, name: org.name, email: org.email }))
    },
  }
}

/**
 * Add an email to the timeline activity store
 */
async function addEmailToTimeline(email: Email): Promise<void> {
  const timelineStore = useTimelineActivityStore()

  // Idempotency: skip if a timeline activity already exists for this email
  try {
    const existing = await timelineStore.query([
      { field: 'entityId', op: '==', value: email.id! },
      { field: 'entityType', op: '==', value: 'email' },
    ])
    if (existing.length > 0) return
  } catch {
    // If query fails, proceed to attempt add; errors will be handled by store
  }

  await timelineStore.add({
    type: 'email',
    title: email.message.subject || 'Email',
    description: email.message.text || '',
    createdAt: email.sentAt,
    personId: email.personId,
    organizationId: email.organizationId,
    entityId: email.id!,
    entityType: 'email',
    metadata: {
      to: email.to,
      from: email.from,
    },
  })
}
